/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DataFormModel } from '../../state/dataForm.model';
import { SetParcelImageUrlPayload, SetParcelPanoPayload, StreetViewMetadata } from '../../state/report.model';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, filter, map, mergeMap, pluck, takeUntil, tap } from 'rxjs/operators';
import { LoggerService } from '../../../shared/services/logger.service';
import { SetParcelDisplayAddress, SetParcelImageUrl, SetParcelPano } from '../../state/report.actions';
import { Select, Store } from '@ngxs/store';
import { IsolatedReportService } from '../../services/isolatedReport.service';
import { GoogleMapService } from '../../../map/services/googleMap.service';
import { combineLatest, merge, Observable, of, Subject } from 'rxjs';
import { ReportState } from '../../state/report.state';
import { ReportContentBaseComponent } from '../report-content-base.component';
import PlaceResult = google.maps.places.PlaceResult;
import { ImageSize } from '../../../../types';

@Component({
    selector: 'dash-gis-report-content-detail',
    templateUrl: './report-content-detail.component.html',
    styleUrls: ['./report-content-detail.component.scss'],
})
export class ReportContentDetailComponent extends ReportContentBaseComponent implements OnChanges, OnDestroy {
    @Input() public dataFormGroup: UntypedFormGroup;
    @Input() public dataForm: DataFormModel;
    @Input() public tableEditActive: boolean;

    @Output() public streetViewUrl = new EventEmitter<string>();

    private disposeSubject = new Subject<void>();

    @Select(ReportState.parcelImageSize)
    private parcelImageSizeFn$: Observable<(id: string, reportName: string) => ImageSize>;

    @ViewChild('attributions', {
        static: true,
        read: ElementRef,
    })
    private attributions: ElementRef;

    constructor(
        logger: LoggerService,
        isolatedReportService: IsolatedReportService,
        private googleMapService: GoogleMapService,
        private store: Store
    ) {
        super(logger, isolatedReportService);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.dataSource?.currentValue) {
            if (!isEqual(changes.dataSource.currentValue, changes.dataSource.previousValue)) {
                this.isolatedReportService.reportTrigger$.pipe(takeUntil(this.dataSource.disposing$)).subscribe(() => {
                    this.dataSource.loadReportData();
                });

                if (this.definition.parcelAddressField) {
                    this.initStreetView();
                }
            }
        }
    }

    initStreetView() {
        const latLonRegExp = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;

        const locationField$ = this.dataSource.reportRawData$.pipe(
            distinctUntilChanged(isEqual),
            filter((data) => !!data && data.length > 0),
            map((data) => data[0]),
            pluck(this.definition.parcelAddressField)
        );

        const latLon$ = locationField$.pipe(filter((location) => latLonRegExp.test(location)));

        const placeDetail$ = locationField$.pipe(
            // filter(location => !latLonRegExp.test(location)),
            mergeMap((address) => this.googleMapService.getPlaceId$(address)),
            mergeMap((placeId: string) =>
                this.googleMapService.getPlaceDetails$(placeId, ['geometry', 'formatted_address', 'address_components'])
            )
        );
        const metadata$ = merge(latLon$, placeDetail$).pipe(
            map((location: string | PlaceResult) => {
                if (location === null) {
                    return [null, null];
                } else if (typeof location === 'string') {
                    return [location, null];
                } else if ('geometry' in location) {
                    return [location.geometry.location.toUrlValue(), location?.formatted_address];
                }
            }),
            mergeMap(([latLngString, address]: [string, string?]) =>
                this.googleMapService.getStreetViewMetadata$(latLngString, address)
            )
        );

        this.getParcelImage(metadata$);
        this.getParcelAddress(placeDetail$);
    }

    getParcelImage(streetViewMetadata$: Observable<StreetViewMetadata>) {
        const metadata$ = streetViewMetadata$.pipe(
            tap((metadata: StreetViewMetadata) => {
                if (this.googleMapService.apiAvailable && metadata?.status === google.maps.StreetViewStatus.OK) {
                    const payload: SetParcelPanoPayload = {
                        id: this.id,
                        reportName: this.definition.name,
                        pano: metadata.pano_id,
                        heading: metadata.heading,
                    };
                    this.store.dispatch(new SetParcelPano(payload));
                }
            })
        );

        combineLatest([metadata$, this.parcelImageSize$])
            .pipe(
                mergeMap(
                    ([metadata, imageSize]: [
                        StreetViewMetadata,
                        {
                            width: number;
                            height: number;
                        }
                    ]) => {
                        if (imageSize) {
                            metadata.size = `${imageSize.width}x${imageSize.height}`;
                        }
                        return this.googleMapService.apiAvailable &&
                            metadata?.status === google.maps.StreetViewStatus?.OK
                            ? this.googleMapService.getStaticStreetViewUrl$(metadata)
                            : of(this.googleMapService.noStreetViewSrc);
                    }
                ),
                filter((b) => !!b),
                tap((url: string) => {
                    const payload: SetParcelImageUrlPayload = {
                        id: this.id,
                        reportName: this.definition.name,
                        url,
                    };
                    this.store.dispatch(new SetParcelImageUrl(payload));
                }),
                takeUntil(this.disposeSubject.asObservable())
            )
            .subscribe();
    }

    getParcelAddress(placeDetails$: Observable<google.maps.places.PlaceResult>) {
        placeDetails$
            .pipe(
                map((place) => {
                    if (!place?.address_components) {
                        return null;
                    }
                    return this.googleMapService.constructAddressFromComponents(place.address_components);
                }),
                tap((address) =>
                    this.store.dispatch(
                        new SetParcelDisplayAddress({
                            id: this.id,
                            reportName: this.definition.name,
                            address,
                        })
                    )
                ),
                takeUntil(this.disposeSubject.asObservable())
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.disposeSubject.next();
    }

    get columnsToDisplay(): string[] {
        return ['label', 'value'];
    }

    get id() {
        return this.isolatedReportService.id;
    }

    get parcelImageSize$(): Observable<{
        width: number;
        height: number;
    }> {
        return this.parcelImageSizeFn$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            distinctUntilChanged(isEqual)
        );
    }
}
